import React from 'react';
import { FileText, DollarSign, Clock } from 'lucide-react';

const ClientDashboardHome = ({ data, onNavigate }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const getRecentInvoices = () => {
    return data.invoices
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);
  };

  const getOutstandingAmount = () => {
    return data.invoices.reduce((sum, inv) => sum + (inv.amountDue || 0), 0);
  };

  const getPendingQuotes = () => {
    return data.quotes.filter(q => q.status === 'pending').length;
  };

  const getStatusBadgeClasses = (status) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case 'paid':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'overdue':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'payment-pending':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      default:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
    }
  };

  return (
    <div>
      {/* Welcome Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome back!</h2>
        <p className="text-gray-600">Here's what's happening with your account</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-blue-50 rounded-full p-3">
                <FileText size={24} className="text-blue-600" />
              </div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500">Total Invoices</div>
              <div className="text-2xl font-bold text-gray-900">{data.invoices.length}</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-red-50 rounded-full p-3">
                <DollarSign size={24} className="text-red-600" />
              </div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500">Outstanding</div>
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(getOutstandingAmount())}</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-yellow-50 rounded-full p-3">
                <Clock size={24} className="text-yellow-600" />
              </div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500">Pending Quotes</div>
              <div className="text-2xl font-bold text-gray-900">{getPendingQuotes()}</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-cyan-50 rounded-full p-3">
                <FileText size={24} className="text-cyan-600" />
              </div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500">Documents</div>
              <div className="text-2xl font-bold text-gray-900">{data.documents.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h6 className="text-lg font-semibold text-gray-900">Recent Invoices</h6>
                <button
                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors duration-200"
                  onClick={() => onNavigate('invoices')}
                >
                  View All
                </button>
              </div>
            </div>
            <div className="p-6">
              {getRecentInvoices().length === 0 ? (
                <p className="text-gray-500 text-center py-8">No invoices yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getRecentInvoices().map(invoice => (
                        <tr key={invoice._id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{invoice.number}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(invoice.date).toLocaleDateString('en-GB')}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(invoice.totalAmount)}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <span className={getStatusBadgeClasses(invoice.status)}>
                              {invoice.status === 'payment-pending' ? 'Payment Processing' : invoice.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h6 className="text-lg font-semibold text-gray-900">Quick Actions</h6>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <button
                  className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                  onClick={() => onNavigate('invoices')}
                >
                  <FileText size={16} className="mr-2" />
                  View Invoices
                </button>
                <button
                  className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                  onClick={() => onNavigate('quotes')}
                >
                  <DollarSign size={16} className="mr-2" />
                  View Quotes
                </button>
                {data.permissions.accesseWallet && (
                  <button
                    className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                    onClick={() => onNavigate('wallet')}
                  >
                    <DollarSign size={16} className="mr-2" />
                    Top Up Wallet
                  </button>
                )}
                <button
                  className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                  onClick={() => onNavigate('documents')}
                >
                  <FileText size={16} className="mr-2" />
                  View Documents
                </button>
              </div>
            </div>
          </div>

          {/* Recent Documents */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h6 className="text-lg font-semibold text-gray-900">Recent Documents</h6>
                <button
                  className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors duration-200"
                  onClick={() => onNavigate('documents')}
                >
                  View All
                </button>
              </div>
            </div>
            <div className="p-6">
              {data.documents.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No documents yet</p>
              ) : (
                <div className="space-y-4">
                  {data.documents.slice(0, 3).map(doc => (
                    <div key={doc._id} className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <h6 className="text-sm font-medium text-gray-900 truncate">{doc.label}</h6>
                          <p className="text-xs text-gray-500 mt-1">{doc.description}</p>
                        </div>
                        <span className="text-xs text-gray-400 ml-4 flex-shrink-0">
                          {new Date(doc.createdAt).toLocaleDateString('en-GB')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboardHome;